import React from "react";
import { TooltipController } from "../Tooltip";
import * as d3 from "d3";
import _ from "lodash";
import { createSVGElementIfNotExist, getSizeElements } from "./utils";
import ReactDOM from "react-dom";

export interface DataPoint {
    series: string;
    label: string;
    value: number;
}

export interface DataPointWithGraphic extends DataPoint {
    x: number;
    y: number;
    width: number;
    height: number;
    background: string;
}

export const draw = (
    elementId: string,
    ref: HTMLDivElement,
    labels: string[],
    dataPoints: DataPoint[],

    series: string[],
    seriesBackground: string[],
    barHeight: number,
    barGap: number,
    maxValue?: number,
    onHover?: (data: DataPointWithGraphic, tooltip: HTMLElement) => void
    // onHover?: (data: HierarchyNodeElement, tooltip: HTMLElement) => void,
    // onClick?: (data: HierarchyNodeElement) => void
): void => {
    const { width, height } = getSizeElements(ref);
    const element = ref;
    d3.select(element).style("position", "relative");

    // work out the scale
    // because it's stacked bar chart, everything becomes positive
    dataPoints = dataPoints.map(d => {
        return {
            ...d,
            value: Math.abs(d.value ?? 0)
        };
    });

    const labelToDataPoints = _.groupBy(dataPoints, d => d.label);

    const labelsDataPoints: DataPoint[][] = labels.map(label => {
        if (!labelToDataPoints?.[label]) return [];

        const seriesToDataPoint = _.keyBy(labelToDataPoints[label], d => d.series);

        return series
            .map(seri => {
                return seriesToDataPoint[seri];
            })
            ?.filter(d => d != null);
    });

    const max = d3.max([
        ...labelsDataPoints.map(labelDataPoints => {
            return _.sum(labelDataPoints.map(d => d.value ?? 0));
        }),
        maxValue ?? 0
    ]);

    const min = 0;

    const scale = d3
        .scaleLinear()
        .range([0, width]) // unit: km
        .domain([min, max]); // unit: pixels

    const seriesToBackground = _.keyBy(
        series.map((seri, index) => {
            return {
                series: seri,
                background: seriesBackground[index]
            };
        }),
        d => d.series
    );

    // const verticalScale = d3
    //     .scaleLinear()
    //     .domain([0, height]) // unit: km
    //     .range([0, labels?.length]); // unit: pixels

    const labelsDataPointsWithGraphic: DataPointWithGraphic[][] = labelsDataPoints.map(
        (labelDataPoints, labelIndex) => {
            const _labelDataPoints = labelDataPoints.filter(d => d.value != null);

            const xsrcPoints = _labelDataPoints.reduce((prev, cur, index, arr) => {
                if (index === 0) prev = [0];
                else {
                    const newCur = prev[index - 1] + (arr[index - 1]?.value ?? 0);
                    prev = [...prev, newCur];
                }

                return prev;
            }, [] as number[]);

            return _labelDataPoints.map((dataPoint, index) => {
                const x0 = xsrcPoints[index];
                const x1 = xsrcPoints[index] + (dataPoint.value ?? 0);
                const y = labelIndex * (barHeight + barGap);

                return {
                    ...dataPoint,
                    x: scale(x0),
                    y,
                    width: scale(x1) - scale(x0),
                    height: barHeight,
                    background: seriesToBackground?.[dataPoint?.series]?.background ?? "black"
                };
            });
        }
    );

    const tooltip = new TooltipController(`tooltip-${elementId}`);
    tooltip.create();

    // insert svg if it doesn't exist
    let svg = createSVGElementIfNotExist(
        !d3.select(element).select("svg").nodes().length,
        element,
        element => {
            return d3.select(element).append("svg");
        },
        "svg"
    ) as d3.Selection<SVGSVGElement, unknown, null, undefined>;

    svg = svg.attr("width", width).attr("height", height);

    // insert grid if it doesn't exist
    const grid = createSVGElementIfNotExist(
        !svg.select(".grid").nodes().length,
        svg.node() as unknown as HTMLElement,
        element => {
            return d3.select(element).append("g").classed("grid", true);
        },
        ".grid"
    ) as d3.Selection<SVGGElement, unknown, null, undefined>;

    // create bar groups
    let groups = grid.selectAll(".group").data(labels as string[], d => d as string) as d3.Selection<
        SVGGElement,
        string,
        null,
        undefined
    >;
    groups.exit().remove();

    const enterGroupEls = groups.enter().append("g").classed("group", true);

    groups = groups.merge(enterGroupEls);

    // create bars in each group
    let bars = groups.selectAll(".bar").data(
        function (label: string, index: number) {
            const dataPoints = labelsDataPointsWithGraphic?.[index];
            return dataPoints;
        },
        d => (d as DataPointWithGraphic)?.series
    ) as d3.Selection<SVGRectElement, DataPointWithGraphic, null, undefined>;

    bars.exit().remove();

    const enterGroupBars = bars.enter().append("rect").classed("bar", true);
    bars = bars.merge(enterGroupBars);

    bars = bars
        // .attr("x", d => d.x)
        .attr("y", d => d.y)
        // .attr("width", d => d.width)
        .attr("height", d => d.height)
        .style("fill", d => d.background);

    bars.transition()
        .duration(800)
        .attrTween("x", function (d) {
            if (!this["_current"]) {
                this["_current"] = {
                    width: 0,
                    x: 0
                } as {
                    width: number;
                    x: number;
                };
            }

            const interpolate = d3.interpolate(this["_current"], {
                width: d.width,
                x: d.x
            });

            this["_current"] = interpolate(0);
            return function (t) {
                const { x } = interpolate(t);
                return `${x}`;
            };
        })
        .attrTween("width", function (d) {
            if (!this["_current"]) {
                this["_current"] = {
                    width: 0,
                    x: 0
                } as {
                    width: number;
                    x: number;
                };
            }

            const interpolate = d3.interpolate(this["_current"], {
                width: d.width,
                x: d.x
            });

            this["_current"] = interpolate(0);
            return function (t) {
                const { width } = interpolate(t);
                return `${width}`;
            };
        });

    // setup hover effect for arcs
    bars.on("mouseover", function (d) {
        d3.select(this).classed("hovered", true);

        tooltip.displayTooltip();
    })
        .on("mousemove", function (d) {
            const data = d as DataPointWithGraphic;

            if (onHover) onHover(data, tooltip.getElement() as HTMLElement);
            else {
                ReactDOM.render(
                    <div>
                        <span>
                            {data.label}: {data.value}{" "}
                        </span>
                    </div>,
                    tooltip.getElement() as HTMLElement
                );
            }

            tooltip.locateTooltip(d3.event);
        })
        .on("mouseleave", function () {
            d3.select(this).classed("arc-hovered", false);
            tooltip.hideTooltip();
            // descriptionRef.style.pointerEvents = "auto";
        });
};
